import { Link } from 'react-router-dom';
import { useT } from '../lib/idioma.js';

import { caminhoDaArea } from '../lib/endereco.js';

/**
 * As peças do `/entrar` que a PRÉVIA da bancada também desenha (leva L2): o bloco
 * «É a sua primeira vez?» e o rodapé. Partilhadas, e não copiadas — a prévia
 * que diverge da tela é a única coisa que uma prévia não pode ser (C7).
 */
export function SecundarioDoEntrar({
  realcar = false,
  busca = '',
  cadastroAberto = false,
  slug,
}: {
  realcar?: boolean;
  busca?: string;
  /**
   * A porta pública de cadastro está aberta nesta área? — `CE3`.
   *
   * **Padrão `false`, e o padrão é a decisão.** Um link para
   * `/a/<slug>/cadastro` numa área de porta fechada leva a um 404 — e o 404 é
   * deliberado (`Z4`: ele não confirma que a área existe). Desenhar o link por
   * omissão faria a tela prometer uma porta que o servidor recusa, e quem
   * clicasse concluiria que o curso não existe.
   */
  cadastroAberto?: boolean;
  /** Sem ele não há para onde apontar; o bloco do cadastro simplesmente não sai. */
  slug?: string | null;
}) {
  const t = useT();
  return (
    <>
    <div
      className="mt-6 px-4 py-4"
      style={{
        borderRadius: 'var(--raio)',
        border: `1px solid ${realcar ? 'var(--destaque)' : 'transparent'}`,
        background: 'var(--superficie)',
      }}
    >
      {/*
        ── O TÍTULO NOMEIA O OBJETO, E NÃO O INEDITISMO (`CE4`) ──────────────

        Dizia «É a sua primeira vez aqui?», e medido no navegador em 21/09/2026
        isso descreve o público ERRADO. Quem comprou e recebeu um código lê
        aquilo e diz «sim, é»; quem nunca comprou nada lê a MESMA frase e também
        diz «sim, é». O desempate entre este bloco e a porta do cadastro, logo
        abaixo, ficava todo no parágrafo de apoio — que só este bloco tem, e que
        a maioria não lê.

        «Recebeu um código de acesso?» separa os dois na PRIMEIRA linha, porque
        pergunta por uma coisa que a pessoa tem ou não tem na mão. Quem não
        recebeu código nenhum já sabe que não é aqui, sem ler mais nada.
      */}
      <p className="text-sm font-medium">{t('acesso.recebeuCodigo')}</p>
      <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--texto-suave)' }}>
        {t('acesso.quemNaoTemSenha')}
      </p>
      <Link
        to={`/primeiro-acesso${busca}`}
        className="mt-3 inline-block px-4 py-2.5 text-sm font-medium"
        style={{
          background: 'var(--destaque)',
          color: 'var(--sobre-destaque, #08130c)',
          borderRadius: 'var(--raio)',
        }}
      >
        {t('acesso.entrarComCodigo')}
      </Link>
    </div>

    {/*
      ── A PORTA DO CADASTRO, VISTA DA TELA DE ENTRAR (`CE3`) ─────────────────

      A ordem do dono, em 20/09/2026: *«falta a partir da tela de login fazer um
      cadastro»*. O cadastro espontâneo existia desde `CE1` e só era alcançável
      pelo endereço que o tutor PUBLICA — quem chegasse a `/entrar` sem conta
      não tinha gesto nenhum, e a tela oferecia-lhe três caminhos que todos
      pressupõem já ter acesso (senha, código, esqueci a senha).

      ## Por que é um bloco à parte, e não uma segunda linha no de cima

      Porque o de cima é para quem **comprou** e ainda não definiu senha — é o
      que a frase dele diz, «o código de acesso que recebeu». Este é para quem
      não comprou nada. Juntá-los faria a pessoa com um código na mão hesitar
      entre dois botões, que é o único desfecho pior do que não ter botão.

      ## Por que ele NÃO é o gesto realçado

      Porque quem volta é a maioria, e a senha continua a ser o primeiro
      formulário — a mesma hierarquia que o cabeçalho de `Entrar` argumenta. Este
      é de fio, não sólido: presente para quem o procura, sem competir com o
      gesto de quem já tem conta.
    */}
    {cadastroAberto && slug ? (
      <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1" data-vst-regiao="porta-do-cadastro">
        <p className="text-sm" style={{ color: 'var(--texto-suave)' }}>
          {t('acesso.semConta')}
        </p>
        <Link
          to={caminhoDaArea(slug, '/cadastro')}
          className="text-sm font-medium underline"
          style={{ color: 'var(--destaque)' }}
        >
          {t('acesso.criarMinhaConta')}
        </Link>
      </div>
    ) : null}
    </>
  );
}

/**
 * ── «ESQUECI MINHA SENHA» VOLTA PARA JUNTO DO CAMPO SENHA (`CE4`) ───────────
 *
 * Ela era o `rodape` da tela, e o `rodape` é a ÚLTIMA peça que `Corpo` desenha
 * — depois do formulário e depois do bloco secundário. Medido no navegador em
 * 21/09/2026, a ordem que saía era: campo Senha, botão Entrar, cartão do código,
 * porta do cadastro, e só então a recuperação, em cinza. Quem esqueceu a senha
 * varria a coluna inteira e passava por **duas ofertas de criar conta** antes de
 * encontrar o que veio buscar — e as duas são, para ele, a resposta errada.
 *
 * Agora ela entra DENTRO do `formulario`, logo debaixo do campo a que pertence.
 * O `formulario` é a peça que as seis estruturas desenham no mesmo lugar
 * (`Basicas.tsx` e `Estruturas.tsx` chamam `Corpo`, e a Faixa só reposiciona o
 * `secundario`), então a recuperação passa a ficar colada ao campo Senha em
 * todas as seis, e não só nas duas de sempre.
 *
 * **Continua discreta de propósito.** Alinhada à direita, do tamanho do texto de
 * apoio e na cor dele: quem volta e sabe a senha não deve ser desviado por ela.
 * O que mudou é a DISTÂNCIA até quem precisa dela, não o peso.
 *
 * O nome do link não mudou, e não podia: `routes/auth.ts` e
 * `routes/aluno-cadastro.ts` mandam o operador «usar «Esqueci minha senha» na
 * tela de entrar» por escrito, e uma cerca em
 * `routes/adversario-recuperacao-de-senha.test.ts` casa essa frase.
 */
export function EsqueciASenha({ busca = '' }: { busca?: string }) {
  const t = useT();
  return (
    /* `-mt-1` contra o `gap-3` do formulário: ela é do campo de cima, e o espaço
       de um irmão qualquer a faria parecer um terceiro campo. */
    <div className="-mt-1 text-right">
      <Link
        to={`/esqueci-senha${busca}`}
        className="text-sm underline"
        style={{ color: 'var(--texto-suave)' }}
      >
        {t('acesso.esqueciSenha')}
      </Link>
    </div>
  );
}
